import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAxiosClient from "../axios-client";
import { useStateContext } from "../contexts/ContextProvider";
import { holdReasonLabel, statusMeta, fmtDateTime, hexToRgba } from "../components/Order/orderPrimitives";

/**
 * Teams Dashboard — a cross-team leaderboard imported from the Claude Design
 * project ("Teams Dashboard.dc.html"). Admins see every team; a regular member
 * is scoped to their own team by the backend (/dashboard/teams). Each row ranks
 * a team by revenue / order volume / delivery rate and expands to its top
 * members and order-source mix. Theme-aware (dark + light) per the app's design
 * convention; the design's own top-bar chrome is dropped since the app supplies
 * its header and sidebar.
 */

const PRIMARY = (() => { try { return localStorage.getItem("ACCENT_COLOR") || "#d97706"; } catch { return "#d97706"; } })();

const palette = (dark) =>
  dark
    ? { bg: "#0a0d13", surface: "#11151c", surface2: "#1a1f29", border: "#232935", text: "#e8ebf1", muted: "#8a93a3", dim: "#616b7c" }
    : { bg: "#f8fafc", surface: "#ffffff", surface2: "#f1f5f9", border: "#e2e8f0", text: "#0f172a", muted: "#64748b", dim: "#94a3b8" };

const MONO = "'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace";
const SANS = "'Outfit', system-ui, -apple-system, 'Segoe UI', sans-serif";

const RANGES = [
  { key: "month", label: "This Month", sub: "this month" },
  { key: "today", label: "Today", sub: "today" },
  { key: "last30", label: "Last 30 days", sub: "in range" },
];

const SORTS = [
  ["revenue", "Revenue"],
  ["orders", "Order volume"],
  ["rate", "Delivery rate"],
];

const AVATAR_PALETTE = ["#3b82f6", "#8b5cf6", "#0ea5e9", "#f59e0b", "#10b981", "#ec4899", "#6366f1"];
const MIX_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#0ea5e9", "#10b981"];

/**
 * The triage tiles, in the Overview board's reading and colour order so the two
 * boards say the same thing the same way. `bucket` is the key the Orders list
 * knows, which is what the tile filters to on the way through.
 */
const TRIAGE_TILES = [
  { key: "overdue", label: "Overdue", note: "past promised", icon: "!", color: "#f87171" },
  { key: "dueToday", label: "Due today", note: "promised today", icon: "◷", color: "#fbbf24" },
  { key: "inProduction", label: "In production", note: "with the factory", icon: "◍", color: "#fbbf24" },
  { key: "readyToDeliver", label: "Ready to deliver", note: "awaiting pickup", icon: "▲", color: "#34d399" },
];

/** ON HOLD. Named so the hold section reads as a status, not an 11. */
const ON_HOLD = 11;

const fmtBDT = (n) => "BDT " + Math.round(Number(n) || 0).toLocaleString("en-US");
const fmtShort = (n) => {
  n = Math.round(Number(n) || 0);
  if (n >= 1000000) return "BDT " + (n / 1000000).toFixed(2) + "M";
  if (n >= 1000) return "BDT " + (n / 1000).toFixed(0) + "K";
  return "BDT " + n.toLocaleString("en-US");
};
const initials = (s) => (s || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const avatarBg = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};
const pct = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : cur > 0 ? 100 : 0);

const rangeParams = (key) => {
  if (key === "today") return { range: "today" };
  if (key === "last30") {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 29);
    const iso = (d) => d.toISOString().slice(0, 10);
    return { range: "custom", from: iso(from), to: iso(to) };
  }
  return { range: "month" };
};

const TeamsDashboard = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { darkMode } = useStateContext();
  const c = palette(darkMode);

  const [range, setRange] = useState("month");
  const [sort, setSort] = useState("revenue");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [rangeMeta, setRangeMeta] = useState(null);
  // Scoped to the viewer by the server, not to the rows above: all teams for an
  // admin, own team for a team lead, own orders otherwise.
  const [hold, setHold] = useState(null);
  const [delayedProduction, setDelayedProduction] = useState(null);
  // Scoped like the hold section: to the viewer, not to the rows below.
  const [triage, setTriage] = useState(null);

  const fetchData = useCallback(
    (rangeKey) => {
      setLoading(true);
      const params = new URLSearchParams(rangeParams(rangeKey)).toString();
      axiosClient
        .get(`/dashboard/teams?${params}`)
        .then(({ data }) => {
          setTeams(data.teams || []);
          setRangeMeta(data.range || null);
          setHold(data.hold || null);
          setDelayedProduction(data.delayedProduction || null);
          setTriage(data.triage || null);
          setLoading(false);
        })
        .catch((error) => {
          const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
          toast.error(message);
          setLoading(false);
        });
    },
    [axiosClient]
  );

  useEffect(() => {
    fetchData(range);
  }, [range, fetchData]);

  const rate = (t) => (t.promised > 0 ? (t.delivered / t.promised) * 100 : 0);

  // KPI totals (mirrors the design's client-side aggregation).
  const kpis = useMemo(() => {
    const sum = (k) => teams.reduce((n, t) => n + (t[k] || 0), 0);
    const totalOrders = sum("orders");
    const totalRevenue = sum("revenue");
    const totalDelivered = sum("delivered");
    const totalPromised = sum("promised");
    const prevOrders = sum("prevOrders");
    const prevRevenue = sum("prevRevenue");
    const prevDelivered = sum("prevDelivered");
    const prevPromised = sum("prevPromised");
    const avgRate = totalPromised ? (totalDelivered / totalPromised) * 100 : 0;
    const prevRate = prevPromised ? (prevDelivered / prevPromised) * 100 : 0;
    const activeTeams = teams.filter((t) => t.orders > 0).length;
    const totalMembers = sum("members");
    const delta = (v) => (v > 0 ? "+" : "") + v.toFixed(1) + "%";
    const dcolor = (v) => (v >= 0 ? "#4ade80" : "#fca5a5");
    const oD = pct(totalOrders, prevOrders);
    const rD = pct(totalRevenue, prevRevenue);
    const rateD = avgRate - prevRate;
    return {
      totalOrders,
      totalRevenue,
      totalMembers,
      tiles: [
        { icon: "▤", iconBg: "rgba(59,130,246,.12)", iconColor: "#60a5fa", label: "Total orders", value: totalOrders.toLocaleString("en-US"), delta: delta(oD), deltaColor: dcolor(oD), sub: "across " + teams.length + " teams" },
        { icon: "৳", iconBg: "rgba(16,185,129,.12)", iconColor: "#34d399", label: "Total revenue", value: fmtShort(totalRevenue), delta: delta(rD), deltaColor: dcolor(rD), sub: fmtBDT(totalRevenue) },
        { icon: "✓", iconBg: "rgba(139,92,246,.12)", iconColor: "#a78bfa", label: "Avg delivery rate", value: avgRate.toFixed(1) + "%", delta: (rateD >= 0 ? "+" : "") + rateD.toFixed(1) + "pp", deltaColor: dcolor(rateD), sub: totalDelivered.toLocaleString("en-US") + " of " + totalPromised.toLocaleString("en-US") + " promised" },
        { icon: "◈", iconBg: "rgba(245,158,11,.12)", iconColor: "#fbbf24", label: "Active teams", value: activeTeams + " / " + teams.length, delta: "", deltaColor: c.muted, sub: teams.length - activeTeams + " with no orders in range" },
        { icon: "☰", iconBg: "rgba(14,165,233,.12)", iconColor: "#38bdf8", label: "Total members", value: String(totalMembers), delta: "", deltaColor: c.muted, sub: "avg " + (totalOrders / Math.max(1, totalMembers)).toFixed(1) + " orders / member" },
      ],
    };
  }, [teams, c.muted]);

  const sorted = useMemo(() => {
    const key = sort === "revenue" ? "revenue" : sort === "orders" ? "orders" : "rate";
    return [...teams].sort((a, b) => (key === "rate" ? rate(b) - rate(a) : b[key] - a[key]));
  }, [teams, sort]);

  // Default the expanded row to the current leader once data is present.
  useEffect(() => {
    if (!loading && sorted.length && (expanded == null || !sorted.some((t) => t.id === expanded))) {
      setExpanded(sorted[0].id);
    }
  }, [loading, sorted, expanded]);

  const seg = (active) => ({ background: active ? PRIMARY : "transparent", color: active ? "#fff" : c.muted });
  const boardRange = rangeMeta ? `${rangeMeta.from} → ${rangeMeta.to}` : "";
  // The app's ON HOLD colour, theme-aware — the same one the status badge uses
  // everywhere else, rather than a purple chosen for this screen.
  const holdHue = statusMeta(ON_HOLD, darkMode).color;

  /**
   * The triage tiles plus the hold count, which belongs beside them: held work
   * is open work that has stopped, and without it a board with six stuck orders
   * and nothing overdue would read "All caught up".
   */
  const attentionTiles = triage
    ? [
        ...TRIAGE_TILES.map((t) => {
          const count = triage[t.key === "readyToDeliver" ? "ready" : t.key] || 0;
          if (t.key === "inProduction" && triage.productionDelayed > 0) {
            return {
              ...t,
              count,
              note: `${triage.productionDelayed} delayed > 48h`,
              color: "#f87171",
            };
          }
          return { ...t, count };
        }),
        { key: "onHold", label: "On hold", note: "not moving", icon: "⏸", color: holdHue, count: hold?.total || 0 },
      ]
    : [];
  const attentionTotal = attentionTiles.reduce((n, t) => n + t.count, 0);
  const rangeSub = RANGES.find((r) => r.key === range)?.sub || "";

  const cols = "44px minmax(200px,1.5fr) 110px minmax(210px,1.3fr) minmax(240px,1.15fr) 92px 108px";
  const rankStyles = [
    { rankBg: "rgba(217,164,65,.14)", rankBorder: "rgba(217,164,65,.35)", rankColor: "#e3b45f" },
    { rankBg: "rgba(154,166,184,.12)", rankBorder: "rgba(154,166,184,.3)", rankColor: "#b6c0cf" },
    { rankBg: "rgba(176,125,79,.14)", rankBorder: "rgba(176,125,79,.32)", rankColor: "#c99268" },
  ];

  const headers = [
    { label: "#", align: "left", padLeft: "0", padRight: "0" },
    { label: "Team", align: "left", padLeft: "0", padRight: "0" },
    { label: "Orders", align: "right", padLeft: "0", padRight: "0" },
    { label: "Revenue", align: "right", padLeft: "0", padRight: "14px" },
    { label: "Delivery rate", align: "left", padLeft: "20px", padRight: "0" },
    { label: "Trend", align: "right", padLeft: "0", padRight: "0" },
    { label: "", align: "right", padLeft: "0", padRight: "0" },
  ];

  return (
    <div className="td-root" style={{ background: c.bg, color: c.text, fontFamily: SANS, borderRadius: 14, padding: "22px 22px 30px", display: "flex", flexDirection: "column", gap: 20 }}>
      <style>{`
        @keyframes tdPulse{0%,100%{opacity:.35}50%{opacity:.7}}
        @keyframes tdFade{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:none}}
        .td-row:hover{background:${c.surface2} !important}
        @media (max-width:640px){
          .td-root{padding:14px 12px 22px !important;border-radius:10px !important}
          .td-ranges{flex-wrap:wrap !important}
        }
      `}</style>

      {/* Header + range selector */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 14 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.text }}>Teams</div>
          <div style={{ fontSize: 13, color: c.muted, marginTop: 3 }}>
            {teams.length} teams · {kpis.totalMembers} members · {kpis.totalOrders.toLocaleString("en-US")} orders {rangeSub}
          </div>
        </div>
        <div className="td-ranges" style={{ display: "flex", background: c.surface, border: `1px solid ${c.border}`, borderRadius: 9, padding: 3, gap: 2 }}>
          {RANGES.map((r) => {
            const s = seg(range === r.key);
            return (
              <div key={r.key} onClick={() => setRange(r.key)} style={{ padding: "0 14px", height: 30, display: "flex", alignItems: "center", borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: "pointer", ...s }}>
                {r.label}
              </div>
            );
          })}
        </div>
      </div>

      {/* KPI tiles — fluid: wraps from 5 across down to 1 on narrow screens */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14 }}>
        {kpis.tiles.map((k, i) => (
          <div key={i} style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 26, height: 26, borderRadius: 7, background: k.iconBg, color: k.iconColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, flexShrink: 0 }}>{k.icon}</span>
              <span style={{ fontSize: 12, color: c.muted, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{k.label}</span>
            </div>
            {loading ? (
              <div style={{ height: 24, width: "74%", borderRadius: 5, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite" }} />
            ) : (
              <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                <div style={{ fontSize: 23, fontWeight: 700, color: c.text, fontFamily: MONO, lineHeight: 1 }}>{k.value}</div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: k.deltaColor }}>{k.delta}</div>
              </div>
            )}
            {loading ? (
              <div style={{ height: 11, width: "58%", borderRadius: 4, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite" }} />
            ) : (
              <div style={{ fontSize: 11, color: c.dim }}>{k.sub}</div>
            )}
          </div>
        ))}
      </div>

      {/* Needs attention — the same card the Overview board leads with, scoped to
          this viewer. A snapshot of now: these tiles ignore the range selector
          above them, which is what the "right now" note says. */}
      {!loading && triage && (
      <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: "15px 17px", display: "flex", flexDirection: "column", gap: 13 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Needs attention</div>
            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
              {attentionTotal === 0 ? "Nothing open right now" : `${attentionTotal.toLocaleString("en-US")} open · right now, not ${boardRange}`}
            </div>
          </div>
          <div onClick={() => navigate("/orders", { state: { bucket: "all" } })} style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}>All orders →</div>
        </div>

        {attentionTotal === 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "10px 4px" }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, background: "rgba(16,185,129,.12)", color: "#34d399", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>✓</span>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text }}>All caught up</div>
              <div style={{ fontSize: 12.5, color: c.muted, marginTop: 2 }}>Nothing overdue, nothing waiting on production, nothing held.</div>
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
            {attentionTiles.map((b) => (
              <div
                key={b.key}
                onClick={() => navigate("/orders", { state: { bucket: b.key } })}
                style={{ border: `1px solid ${hexToRgba(b.color, 0.22)}`, background: hexToRgba(b.color, 0.05), borderRadius: 11, padding: "12px 13px", display: "flex", flexDirection: "column", gap: 9, cursor: "pointer", minWidth: 0 }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ width: 24, height: 24, borderRadius: 7, background: hexToRgba(b.color, 0.14), color: b.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}>{b.icon}</span>
                  <span style={{ fontSize: 10.5, color: c.dim }}>↗</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: b.color, fontFamily: MONO, lineHeight: 1 }}>{b.count.toLocaleString("en-US")}</span>
                  <span style={{ fontSize: 11, color: c.dim }}>{b.note}</span>
                </div>
                <div style={{ fontSize: 11.5, color: c.muted, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Leaderboard */}
      <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Team leaderboard</div>
            <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
              Ranked by {sort === "revenue" ? "revenue" : sort === "orders" ? "order volume" : "delivery rate"} · {boardRange}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ fontSize: 12, color: c.muted }}>Sort by</span>
            <div style={{ display: "flex", background: c.surface2, border: `1px solid ${c.border}`, borderRadius: 8, padding: 3, gap: 2 }}>
              {SORTS.map(([k, l]) => {
                const s = seg(sort === k);
                return (
                  <div key={k} onClick={() => setSort(k)} style={{ padding: "0 12px", height: 28, display: "flex", alignItems: "center", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", ...s }}>
                    {l}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 980 }}>
            {/* Column headers */}
            <div style={{ display: "grid", gridTemplateColumns: cols, padding: "10px 18px", background: c.surface2, borderBottom: `1px solid ${c.border}` }}>
              {headers.map((h, i) => (
                <div key={i} style={{ fontSize: 11, fontWeight: 700, color: c.muted, textTransform: "uppercase", letterSpacing: ".04em", textAlign: h.align, padding: `0 ${h.padRight} 0 ${h.padLeft}` }}>
                  {h.label}
                </div>
              ))}
            </div>

            {loading
              ? [0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: cols, padding: "20px 18px", borderBottom: `1px solid ${c.border}`, alignItems: "center" }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite" }} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                      <div style={{ height: 13, width: 120, borderRadius: 4, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite" }} />
                      <div style={{ height: 10, width: 70, borderRadius: 4, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite" }} />
                    </div>
                    <div style={{ height: 13, width: 52, borderRadius: 4, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite", justifySelf: "end" }} />
                    <div style={{ height: 13, width: 96, borderRadius: 4, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite", justifySelf: "end" }} />
                    <div style={{ height: 30, borderRadius: 6, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite" }} />
                    <div style={{ height: 13, width: 46, borderRadius: 4, background: c.surface2, animation: "tdPulse 1.3s ease-in-out infinite", justifySelf: "end" }} />
                    <div />
                  </div>
                ))
              : sorted.map((t, i) => {
                  const r = rate(t);
                  const C = 2 * Math.PI * 17;
                  const empty = t.orders === 0;
                  const ringColor = empty ? c.surface2 : r >= 92 ? "#34d399" : r >= 85 ? "#fbbf24" : "#f87171";
                  const spark = t.spark && t.spark.length ? t.spark : [0, 0];
                  const mx = Math.max(...spark), mn = Math.min(...spark);
                  const pts = spark.map((v, j) => `${(((j / Math.max(1, spark.length - 1)) * 76) + 1).toFixed(1)},${(24 - ((v - mn) / Math.max(1, mx - mn)) * 22).toFixed(1)}`).join(" ");
                  const rk = rankStyles[i] || { rankBg: c.surface2, rankBorder: c.border, rankColor: c.muted };
                  const mixTotal = (t.mix || []).reduce((n, m) => n + m[2], 0) || 1;
                  const trend = pct(t.orders, t.prevOrders);
                  const flat = empty || Math.abs(trend) < 0.05;
                  const trendUp = trend > 0;
                  const isExpanded = expanded === t.id;
                  return (
                    <div key={t.id} style={{ borderBottom: `1px solid ${c.border}`, background: isExpanded ? "rgba(59,130,246,.05)" : "transparent" }}>
                      <div className="td-row" onClick={() => setExpanded(isExpanded ? null : t.id)} style={{ display: "grid", gridTemplateColumns: cols, padding: "16px 18px", alignItems: "center", cursor: "pointer" }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: rk.rankBg, border: `1px solid ${rk.rankBorder}`, color: rk.rankColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700, fontFamily: MONO }}>{i + 1}</div>

                        <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
                          <span style={{ width: 34, height: 34, borderRadius: 9, background: empty ? c.surface2 : "rgba(59,130,246,.14)", color: empty ? c.dim : "#7db4ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{initials(t.name)}</span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</div>
                            <div style={{ fontSize: 11.5, color: c.muted, marginTop: 2 }}>{t.members} members · Lead {t.lead}</div>
                          </div>
                        </div>

                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: empty ? c.dim : c.text, fontFamily: MONO }}>{empty ? "—" : t.orders.toLocaleString("en-US")}</div>
                          <div style={{ fontSize: 11, color: c.dim, marginTop: 2 }}>{empty ? "no orders in range" : "orders"}</div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12 }}>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: empty ? c.dim : c.text, fontFamily: MONO }}>{empty ? "—" : fmtBDT(t.revenue)}</div>
                            <div style={{ fontSize: 11, color: c.dim, marginTop: 2 }}>{empty ? "no revenue in range" : "avg " + fmtBDT(t.revenue / Math.max(1, t.orders)) + " / order"}</div>
                          </div>
                          <svg width="78" height="26" viewBox="0 0 78 26" style={{ flexShrink: 0, display: empty ? "none" : "block" }}>
                            <polyline points={pts} fill="none" stroke={trend >= 0 ? "#34d399" : "#f87171"} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingLeft: 20 }}>
                          <div style={{ position: "relative", width: 42, height: 42, flexShrink: 0 }}>
                            <svg width="42" height="42" viewBox="0 0 42 42">
                              <circle cx="21" cy="21" r="17" fill="none" stroke={c.surface2} strokeWidth="4" />
                              <circle cx="21" cy="21" r="17" fill="none" stroke={ringColor} strokeWidth="4" strokeLinecap="round" strokeDasharray={empty ? `0 ${C}` : `${((C * r) / 100).toFixed(1)} ${C}`} transform="rotate(-90 21 21)" />
                            </svg>
                            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10.5, fontWeight: 700, color: empty ? c.dim : c.text, fontFamily: MONO }}>{empty ? "—" : Math.round(r) + "%"}</div>
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 11.5, color: c.muted }}>{empty ? "0 of 0 promised" : t.delivered.toLocaleString("en-US") + " of " + t.promised.toLocaleString("en-US") + " promised"}</div>
                            <div style={{ display: "flex", gap: 10, marginTop: 3 }}>
                              <span style={{ fontSize: 11, color: c.dim }}>Cancelled <span style={{ color: "#fca5a5", fontWeight: 600 }}>{t.cancelled}</span></span>
                              <span style={{ fontSize: 11, color: c.dim }}>Returned <span style={{ color: PRIMARY, fontWeight: 600 }}>{t.returned}</span></span>
                            </div>
                          </div>
                        </div>

                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 12.5, fontWeight: 700, color: flat ? c.dim : trendUp ? "#4ade80" : "#fca5a5" }}>{flat ? "—" : (trendUp ? "▲ " : "▼ ") + Math.abs(trend).toFixed(1) + "%"}</div>
                          <div style={{ fontSize: 10.5, color: c.dim, marginTop: 2 }}>vs prev</div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                          <span style={{ fontSize: 11, color: c.muted, opacity: 0.8 }}>{isExpanded ? "Hide" : "Details"}</span>
                          <span style={{ width: 24, height: 24, borderRadius: 6, border: `1px solid ${c.border}`, background: c.surface2, color: c.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform .18s ease" }}>▼</span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div style={{ padding: "4px 18px 22px", animation: "tdFade .18s ease" }}>
                          <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12, padding: "18px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 28 }}>
                            {/* Top members */}
                            <div>
                              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 700, color: c.text }}>Top members</div>
                                <div style={{ fontSize: 11, color: c.dim }}>{boardRange}</div>
                              </div>
                              {t.people && t.people.length ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                  {t.people.slice(0, 5).map((p, j) => (
                                    <div key={j} style={{ display: "grid", gridTemplateColumns: "22px 1fr auto auto", alignItems: "center", gap: 12, padding: "8px 8px", borderRadius: 8 }}>
                                      <span style={{ fontSize: 11, color: c.dim, fontFamily: MONO }}>{j + 1}</span>
                                      <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                                        <span style={{ width: 26, height: 26, borderRadius: "50%", background: avatarBg(p[0]), color: "#fff", fontSize: 10.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{initials(p[0])}</span>
                                        <div style={{ minWidth: 0 }}>
                                          <div style={{ fontSize: 12.5, color: c.text, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p[0]}</div>
                                          <div style={{ fontSize: 10.5, color: c.dim }}>{p[1]}</div>
                                        </div>
                                      </div>
                                      <span style={{ fontSize: 12, color: c.muted, fontFamily: MONO }}>{p[2]} orders</span>
                                      <span style={{ fontSize: 12.5, color: c.text, fontWeight: 600, fontFamily: MONO, minWidth: 96, textAlign: "right" }}>{fmtBDT(p[3])}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div style={{ fontSize: 12.5, color: c.muted, padding: "14px 0" }}>No member activity in this range. {t.members} members on roster.</div>
                              )}
                            </div>

                            {/* Order source mix */}
                            <div>
                              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 700, color: c.text }}>Order source mix</div>
                                <div style={{ fontSize: 11, color: c.dim }}>{empty ? "0 orders" : t.orders.toLocaleString("en-US") + " orders"}</div>
                              </div>
                              {t.mix && t.mix.length ? (
                                <>
                                  <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", gap: 2, marginBottom: 14 }}>
                                    {t.mix.map((m, j) => (
                                      <div key={j} style={{ width: ((m[2] / mixTotal) * 100).toFixed(1) + "%", background: MIX_COLORS[j % MIX_COLORS.length] }} />
                                    ))}
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                                    {t.mix.map((m, j) => (
                                      <div key={j} style={{ display: "grid", gridTemplateColumns: "10px 1fr auto auto", alignItems: "center", gap: 10 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: 2, background: MIX_COLORS[j % MIX_COLORS.length] }} />
                                        <span style={{ fontSize: 12, color: c.text }}>{m[0]}</span>
                                        {m[1] && m[1] !== m[0] ? <span style={{ fontSize: 11, color: c.dim, background: c.surface2, borderRadius: 5, padding: "1px 6px" }}>{m[1]}</span> : <span />}
                                        <span style={{ fontSize: 12, color: c.muted, fontFamily: MONO, minWidth: 88, textAlign: "right" }}>{m[2]} · {fmtShort(m[3])}</span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <div style={{ fontSize: 12.5, color: c.muted, padding: "14px 0" }}>No orders routed to this team in the selected range.</div>
                              )}
                            </div>
                          </div>

                          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                            <button onClick={() => navigate("/orders")} style={{ height: 34, padding: "0 15px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.surface2, color: c.text, fontSize: 12.5, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                              Open {t.name} orders →
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

            {!loading && sorted.length === 0 && (
              <div style={{ padding: "40px 18px", textAlign: "center", fontSize: 13, color: c.muted }}>No teams to show for this range.</div>
            )}
          </div>
        </div>

      </div>

      {/* Stuck on hold — its own card, sibling to the leaderboard rather than a
          block inside it. The leaderboard card clips to its own radius
          (overflow: hidden), so nesting this in there drew the two as one
          joined surface. It answers a different question than the rows above
          (what has stopped, not who sold most) and it ignores the range. */}
      {/* Stuck on hold & Delayed in production side by side */}
      {(!loading && ((hold && hold.tooLong > 0) || (delayedProduction && delayedProduction.total > 0))) && (
        <div style={{ display: "grid", gridTemplateColumns: (hold && hold.tooLong > 0 && delayedProduction && delayedProduction.total > 0) ? "repeat(auto-fit, minmax(420px, 1fr))" : "1fr", gap: 18, alignItems: "start" }}>
          {hold && hold.tooLong > 0 && (
            <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Stuck on hold</div>
                  <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
                    {hold.tooLong} held over {hold.thresholdDays} days · right now, not {boardRange}
                  </div>
                </div>
                <div onClick={() => navigate("/orders", { state: { bucket: "onHold", heldOverDays: hold.thresholdDays } })} style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}>View in orders →</div>
              </div>

              {/* Scrolls at roughly six rows; the rest is a scroll, not a click. */}
              <div style={{ maxHeight: 380, overflowY: "auto", padding: "4px 12px 10px" }}>
                {(hold.stuck || []).map((h, i) => (
                  <div
                    key={h.id}
                    onClick={() => navigate(`/orders/${h.id}`)}
                    style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "baseline", padding: "9px 6px", borderTop: i === 0 ? "none" : `1px solid ${c.border}`, cursor: "pointer", minWidth: 0 }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: c.text, fontFamily: MONO, flexShrink: 0 }}>#{h.id}</span>
                        <span style={{ fontSize: 12.5, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{h.orderedBy}</span>
                        {holdReasonLabel(h.reason) && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: holdHue, background: hexToRgba(holdHue, 0.14), borderRadius: 6, padding: "1px 6px", flexShrink: 0 }}>{holdReasonLabel(h.reason)}</span>
                        )}
                        {h.fromStatus != null && (
                          <span style={{ fontSize: 10.5, color: c.dim, whiteSpace: "nowrap", flexShrink: 0 }}>from {statusMeta(h.fromStatus).label}</span>
                        )}
                        {h.team && (
                          <span style={{ fontSize: 10.5, color: c.dim, whiteSpace: "nowrap", flexShrink: 0 }}>· {h.team}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 11.5, color: h.comment ? c.muted : c.dim, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {h.comment ? `“${h.comment}”` : "No reason recorded"}
                      </div>
                      <div style={{ fontSize: 10.5, color: c.dim, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {[h.by && `held by ${h.by}`, h.since && fmtDateTime(h.since)].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: "#fca5a5", background: "rgba(248,113,113,.12)", borderRadius: 6, padding: "3px 8px", fontFamily: MONO, flexShrink: 0 }}>{h.days}d</span>
                  </div>
                ))}
                {/* Only when the server's cap actually bit. */}
                {hold.tooLong > (hold.stuck || []).length && (
                  <div onClick={() => navigate("/orders", { state: { bucket: "onHold", heldOverDays: hold.thresholdDays } })} style={{ fontSize: 11.5, color: c.muted, cursor: "pointer", padding: "9px 6px 0" }}>
                    +{hold.tooLong - (hold.stuck || []).length} more not shown →
                  </div>
                )}
              </div>
            </div>
          )}

          {delayedProduction && delayedProduction.total > 0 && (
            <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, overflow: "hidden" }}>
              <div style={{ padding: "14px 18px", borderBottom: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: c.text }}>Delayed in production</div>
                  <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
                    {delayedProduction.total} delayed over {delayedProduction.thresholdHours} hours
                    {delayedProduction.byStatus && (
                      <span style={{ marginLeft: 6 }}>
                        ({[
                          delayedProduction.byStatus.print != null && `${delayedProduction.byStatus.print} print`,
                          (delayedProduction.byStatus.cutting ?? delayedProduction.byStatus.production) != null && `${delayedProduction.byStatus.cutting ?? delayedProduction.byStatus.production} cutting`,
                          delayedProduction.byStatus.sewing != null && `${delayedProduction.byStatus.sewing} sewing`,
                        ].filter(Boolean).join(" · ")})
                      </span>
                    )}
                    {" "}· right now, not {boardRange}
                  </div>
                </div>
                <div
                  onClick={() => navigate("/orders", { state: { bucket: "inProduction", delayedOverHours: delayedProduction.thresholdHours } })}
                  style={{ fontSize: 12, fontWeight: 600, color: c.muted, cursor: "pointer" }}
                >
                  View in orders →
                </div>
              </div>

              <div style={{ maxHeight: 380, overflowY: "auto", padding: "4px 12px 10px" }}>
                {(delayedProduction.orders || []).map((o, i) => {
                  const sm = statusMeta(o.status, darkMode);
                  return (
                    <div
                      key={o.id}
                      onClick={() => navigate(`/orders/${o.id}`)}
                      style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10, alignItems: "baseline", padding: "9px 6px", borderTop: i === 0 ? "none" : `1px solid ${c.border}`, cursor: "pointer", minWidth: 0 }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: c.text, fontFamily: MONO, flexShrink: 0 }}>#{o.id}</span>
                          <span style={{ fontSize: 12.5, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.orderedBy}</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: sm.solid || sm.color, background: sm.bg || "rgba(239,68,68,.12)", borderRadius: 6, padding: "1px 6px", flexShrink: 0 }}>
                            {sm.label}
                          </span>
                          {o.team && (
                            <span style={{ fontSize: 10.5, color: c.dim, whiteSpace: "nowrap", flexShrink: 0 }}>· {o.team}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 10.5, color: c.dim, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {o.since ? `in production since ${fmtDateTime(o.since)}` : ""}
                        </div>
                      </div>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: "#fca5a5", background: "rgba(248,113,113,.12)", borderRadius: 6, padding: "3px 8px", fontFamily: MONO, flexShrink: 0 }}>
                        {o.hours}h ({o.days}d)
                      </span>
                    </div>
                  );
                })}
                {delayedProduction.total > (delayedProduction.orders || []).length && (
                  <div
                    onClick={() => navigate("/orders", { state: { bucket: "inProduction", delayedOverHours: delayedProduction.thresholdHours } })}
                    style={{ fontSize: 11.5, color: c.muted, cursor: "pointer", padding: "9px 6px 0" }}
                  >
                    +{delayedProduction.total - (delayedProduction.orders || []).length} more not shown →
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamsDashboard;
